<template lang="pug">
.customer-view
  v-loading(loader = 'customer:fetch')
    .text-center(slot = 'spinner'): i.fa.fa-spinner.fa-pulse.fa-lg

    .animated.fadeIn(v-if = 'customer')
      .customer-view-header
        .d-flex.align-items-center.justify-content-between.pt-2
          .d-flex.align-items-center
            h3.mb-0.mr-3 {{ customerName(customer) }} ({{ customer.idCode }})
            span.badge.badge-secondary.p-2.mr-2(
              v-if = 'isCorporate'
            )
              i.fa.fa-building.mr-1
              | {{ $t("corporate") }}
            span.badge.badge-secondary.p-2.mr-2(
              v-else-if = 'isPrivateCustomer'
            )
              i.fa.fa-user.mr-1
              | {{ $t("private") }}
            template(v-if = 'isVeriffEnabled && isPrivateCustomer && veriffSessionLoaded && !loadingVeriffSession')
              span.badge.p-2.mr-2(
                :class = 'veriffBadgeClass'
                :style = '{ cursor: "pointer" }'
                :title = 'veriffBadgeTitle'
                @click = 'veriffModal = true'
              )
                i.mr-1(:class = 'veriffBadgeIcon')
                | {{ veriffBadgeText }}
          button.btn.btn-sm.btn-outline-primary(
            v-if   = 'isPrivateCustomer && isVeriffEnabled && veriffSessionLoaded && !loadingVeriffSession && !isVeriffVerified'
            @click = 'veriffModal = true'
          )
            i.fa.fa-id-card.mr-1
            | {{ $t('verifyWithVeriff') }}
        ul.nav.mt-2.mb-2
          li.nav-item
            router-link.nav-link.pl-0(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerOverview" }'
            ) {{ $t('overview') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerGeneral" }'
            ) {{ $t('general') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerRoles" }'
            ) {{ $t('roles') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerCommunication" }'
            ) {{ $t('communication') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerLoans" }'
            ) {{ $t('contracts') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerPaymentInfo" }'
            ) {{ $t('paymentInfo') }}
          li.nav-item
            router-link.nav-link(
              active-class = 'text-dark'
              :to          = '{ name: "CustomerReports" }'
            ) {{ $t('reports') }}

      router-view
      customer-veriff-modal(
        v-if      = 'customer && isPrivateCustomer && isVeriffEnabled'
        v-model   = 'veriffModal'
        :customer = 'customer'
        @changed  = 'loadData(id)'
        @input    = 'onModalVisibilityChange'
      )
</template>


<script>
import CustomerVeriffModal from '@/views/customers/CustomerVeriffModal'
import veriffApi from '@/api/veriff'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'customer-view',

  components: { CustomerVeriffModal },

  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },

  i18nOptions: {},

  data: () => ({
    veriffModal: false,
    latestVeriffSession: null,
    loadingVeriffSession: false,
    veriffSessionLoaded: false
  }),

  computed: {
    ...mapState('customers', ['customer']),
    ...mapGetters('customers', ['customerName', 'isCorporateCustomer']),
    ...mapGetters('settings', ['isVeriffEnabled']),
    isCorporate () {
      if (!this.customer) return false
      return Boolean(this.isCorporateCustomer(this.customer) || (this.customer.sectorId && this.customer.sectorId >= 20))
    },
    isPrivateCustomer () {
      return Boolean(this.customer && !this.isCorporate)
    },
    isVeriffVerified () {
      return Boolean(
        (this.customer && this.customer.identityAuthenticated) ||
        (this.latestVeriffSession && this.latestVeriffSession.status === 'APPROVED')
      )
    },
    veriffBadgeClass () {
      if (this.isVeriffVerified) {
        return 'badge-success text-white'
      }
      if (!this.latestVeriffSession) {
        return 'badge-warning text-dark'
      }
      switch (this.latestVeriffSession.status) {
        case 'APPROVED':
          return 'badge-success text-white'
        case 'CREATED':
        case 'EMAIL_SENT':
        case 'STARTED':
        case 'SUBMITTED':
          return 'badge-info text-white'
        case 'DECLINED':
        case 'EXPIRED':
        case 'ABANDONED':
          return 'badge-danger text-white'
        case 'RESUBMISSION_REQUESTED':
          return 'badge-warning text-dark'
        default:
          return 'badge-warning text-dark'
      }
    },
    veriffBadgeIcon () {
      if (this.isVeriffVerified) {
        return 'fa fa-check-circle'
      }
      if (!this.latestVeriffSession) {
        return 'fa fa-exclamation-circle'
      }
      switch (this.latestVeriffSession.status) {
        case 'APPROVED':
          return 'fa fa-check-circle'
        case 'EMAIL_SENT':
          return 'fa fa-envelope'
        case 'CREATED':
        case 'STARTED':
        case 'SUBMITTED':
          return 'fa fa-clock-o'
        case 'DECLINED':
        case 'EXPIRED':
        case 'ABANDONED':
          return 'fa fa-times-circle'
        case 'RESUBMISSION_REQUESTED':
          return 'fa fa-exclamation-triangle'
        default:
          return 'fa fa-exclamation-circle'
      }
    },
    veriffBadgeText () {
      if (this.isVeriffVerified) {
        return this.$t('verified')
      }
      if (!this.latestVeriffSession) {
        return this.$t('unverified')
      }
      switch (this.latestVeriffSession.status) {
        case 'APPROVED':
          return this.$t('verified')
        case 'EMAIL_SENT':
          return this.$t('emailSent')
        case 'CREATED':
        case 'STARTED':
        case 'SUBMITTED':
          return this.$t('inProgress')
        case 'DECLINED':
          return this.$t('declined')
        case 'RESUBMISSION_REQUESTED':
          return this.$t('resubmit')
        case 'EXPIRED':
          return this.$t('expired')
        case 'ABANDONED':
          return this.$t('abandoned')
        default:
          return this.$t('unverified')
      }
    },
    veriffBadgeTitle () {
      if (this.isVeriffVerified) {
        return this.$t('viewVerificationDetails')
      }
      return this.$t('startVerification')
    }
  },

  watch: {
    id: {
      handler: 'loadData',
      immediate: true
    }
  },

  methods: {
    ...mapActions('customers', [
      'loadCustomer',
      'loadCustomerRelations'
    ]),
    ...mapActions('products', ['loadProducts']),
    ...mapActions('settings', ['checkVeriffEnabled']),
    async loadData (customerId) {
      this.veriffSessionLoaded = false
      await this.loadCustomer({ id: customerId })
      await Promise.all([
        this.loadCustomerRelations({ customerId }),
        this.loadProducts({ group: ['LOAN', 'OVERDRAFT', 'LEASING', 'GUARANTEE', 'FACTORING', 'DEPOSIT'] }),
        (async () => {
          await this.checkVeriffEnabled()
          if (this.isVeriffEnabled && this.isPrivateCustomer) {
            await this.loadVeriffSession(customerId)
          } else {
            this.latestVeriffSession = null
            this.veriffSessionLoaded = true
          }
        })()
      ])
    },
    async loadVeriffSession (customerId) {
      this.loadingVeriffSession = true
      try {
        const res = await veriffApi.getLatestVeriffSession({ customerId })
        this.latestVeriffSession = (res && res.data) ? res.data : null
        if (this.latestVeriffSession && this.latestVeriffSession.status === 'APPROVED' && this.customer && !this.customer.identityAuthenticated) {
          await this.loadCustomer({ id: customerId })
        }
      } catch (err) {
        this.latestVeriffSession = null
      } finally {
        this.loadingVeriffSession = false
        this.veriffSessionLoaded = true
      }
    },
    onModalVisibilityChange (visible) {
      if (!visible && this.id) {
        this.loadVeriffSession(this.id)
      }
    }
  }
}
</script>


<i18n>
en:
  overview:         "Overview"
  general:          "General"
  roles:            "Roles"
  communication:    "Communication"
  contracts:        "Contracts"
  paymentInfo:      "Payment info"
  reports:          "Reports"
  verified:         "Identity Verified"
  unverified:       "Not Verified"
  verifyWithVeriff: "Verify Customer"
  corporate:        "Corporate"
  private:          "Private"
  viewVerificationDetails: "Click to view verification details"
  startVerification:       "Click to verify identity"
  inProgress:       "In Progress"
  emailSent:        "Email Sent for Verification"
  declined:         "Declined"
  resubmit:         "Resubmission Requested"
  expired:          "Expired"
  abandoned:        "Abandoned"
et:
  overview:         "Ülevaade"
  general:          "Üldine"
  roles:            "Rollid"
  communication:    "Suhtlus"
  contracts:        "Lepingud"
  paymentInfo:      "Makseinfo"
  reports:          "Aruanded"
  verified:         "Isik tuvastatud"
  unverified:       "Tuvastamata"
  verifyWithVeriff: "Tuvasta Veriffis"
  corporate:        "Äriklient"
  private:          "Eraisik"
  viewVerificationDetails: "Klõpsake tuvastuse detailide vaatamiseks"
  startVerification:       "Klõpsake isiku tuvastamiseks"
  inProgress:       "Pooleli"
  emailSent:        "E-post saadetud tuvastamiseks"
  declined:         "Tagasi lükatud"
  resubmit:         "Nõutud uuesti esitamine"
  expired:          "Aegunud"
  abandoned:        "Katkestatud"
ru:
  overview:         "Обзор"
  general:          "Общее"
  roles:            "Роли"
  communication:    "Связь"
  contracts:        "Договоры"
  paymentInfo:      "Платежи"
  reports:          "Отчеты"
  verified:         "Личность подтверждена"
  unverified:       "Не идентифицирован"
  verifyWithVeriff: "Идентифицировать через Veriff"
  corporate:        "Юр. лицо"
  private:          "Физ. лицо"
  viewVerificationDetails: "Нажмите для просмотра данных идентификации"
  startVerification:       "Нажмите для идентификации личности"
  inProgress:       "В процессе"
  emailSent:        "Письмо отправлено для идентификации"
  declined:         "Отклонено"
  resubmit:         "Требуется повторная подача"
  expired:          "Истек"
  abandoned:        "Прервано"
</i18n>


<style lang='scss' scoped>
  .customer-view-header {
    top: 101px;
    background-color: #e4e5e6;
    position: sticky;
    z-index: 10;
  }
</style>
